import { useEffect, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Card,
  CardContent,
  Input,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { ArrowLeft, Ban, Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { useReference } from '../hooks/useReference'
import { messageOf } from '../lib/apiError'
import { ErrorBanner, StatusBadge } from '../components/catalog'
import { Combobox, Select, Switch } from '../components/controls'
import { Tab, TabCount, Tabs, TabsList, TabsPanel } from '../components/tabs'
import {
  PaymentMethodForm,
  emptyPaymentMethodForm,
  type PaymentMethodFormValue,
} from '../components/paymentMethodForm'
import type { PaymentMethod } from './PaymentMethods'

interface CountryLink {
  countryId: number
  country: string
  isoCode2: string
  isKycNeeded: boolean
  isProhibited: boolean
  creationDate: string
}

interface CausalLink {
  causalId: number
  causal: string
  movementTypeId: number
  movementType: string
  settingTypeId: number
  settingType: string
  creationDate: string
}

const TABS = ['summary', 'countries', 'causals'] as const
type TabValue = (typeof TABS)[number]

/**
 * PaymentMethodDetail is the whole configuration of one payment method, split
 * into tabs because it is three different things: the method's own fields, the
 * countries where it may be used (all 244 of them in production, with per-country
 * KYC and prohibition flags) and the wallet causals behind its deposits and
 * withdrawals.
 *
 * The active tab lives in the URL, so the list page can link straight to the
 * relation it is showing a count for, and a reload keeps you where you were.
 *
 * Unlinking really deletes the row — link rows are assignments, nothing stores
 * their identity — which is why those buttons say "remove", not "disable".
 */
export function PaymentMethodDetailPage() {
  const { t } = useTranslation('app')
  const { id } = useParams()
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')
  const { countries, causals, movementTypes, settingTypes, paymentMethodTypes } = useReference()

  const [searchParams, setSearchParams] = useSearchParams()
  const requested = searchParams.get('tab') as TabValue | null
  const tab: TabValue = requested && TABS.includes(requested) ? requested : 'summary'

  const [countrySearch, setCountrySearch] = useState('')
  const [error, setError] = useState('')
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState<PaymentMethodFormValue>(emptyPaymentMethodForm)
  const [newCountry, setNewCountry] = useState('')
  const [newCausal, setNewCausal] = useState({ causalId: '', movementTypeId: '', settingTypeId: '' })

  const { data: method } = useQuery({
    queryKey: ['payment-methods', id],
    queryFn: async (): Promise<PaymentMethod> => {
      const res = await api.get(`/payment-methods/${id}`)
      return res.data as PaymentMethod
    },
  })

  const { data: countryData } = useQuery({
    queryKey: ['payment-methods', id, 'countries'],
    queryFn: async (): Promise<{ countries: CountryLink[] }> => {
      const res = await api.get(`/payment-methods/${id}/countries`)
      return res.data as { countries: CountryLink[] }
    },
  })

  const { data: causalData } = useQuery({
    queryKey: ['payment-methods', id, 'causals'],
    queryFn: async (): Promise<{ causals: CausalLink[] }> => {
      const res = await api.get(`/payment-methods/${id}/causals`)
      return res.data as { causals: CausalLink[] }
    },
  })

  // Seed the edit form from the loaded method (and re-seed after a save).
  useEffect(() => {
    if (!method) return
    setForm({
      paymentMethodTypeId: String(method.paymentMethodTypeId),
      paymentMethod: method.paymentMethod,
      description: method.description,
      logoPath: method.logoPath,
      icon: method.icon,
    })
  }, [method])

  const invalidate = () => qc.invalidateQueries({ queryKey: ['payment-methods'] })
  const onError = (err: unknown) => setError(messageOf(err, t('paymentMethods.errors.link')))

  const save = useMutation({
    mutationFn: () =>
      api.put(`/payment-methods/${id}`, {
        paymentMethodTypeId: Number(form.paymentMethodTypeId),
        paymentMethod: form.paymentMethod.trim(),
        description: form.description.trim(),
        logoPath: form.logoPath.trim(),
        icon: form.icon.trim(),
        isDisabledByPlatform: method?.isDisabledByPlatform ?? false,
      }),
    onSuccess: () => {
      setEditing(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('paymentMethods.errors.save'))),
  })

  const toggleDisabled = useMutation({
    mutationFn: () =>
      method?.isDisabledByPlatform
        ? api.post(`/payment-methods/${id}/restore`)
        : api.delete(`/payment-methods/${id}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('paymentMethods.errors.save'))),
  })

  const setCountry = useMutation({
    mutationFn: (p: { countryId: number; isKycNeeded: boolean; isProhibited: boolean }) =>
      api.put(`/payment-methods/${id}/countries/${p.countryId}`, {
        isKycNeeded: p.isKycNeeded,
        isProhibited: p.isProhibited,
      }),
    onSuccess: () => {
      setError('')
      setNewCountry('')
      invalidate()
    },
    onError,
  })

  const removeCountry = useMutation({
    mutationFn: (countryId: number) => api.delete(`/payment-methods/${id}/countries/${countryId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const setCausal = useMutation({
    mutationFn: (p: { causalId: number; movementTypeId: number; settingTypeId: number }) =>
      api.put(`/payment-methods/${id}/causals/${p.causalId}`, {
        movementTypeId: p.movementTypeId,
        settingTypeId: p.settingTypeId,
      }),
    onSuccess: () => {
      setError('')
      setNewCausal({ causalId: '', movementTypeId: '', settingTypeId: '' })
      invalidate()
    },
    onError,
  })

  const removeCausal = useMutation({
    mutationFn: (causalId: number) => api.delete(`/payment-methods/${id}/causals/${causalId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError,
  })

  const countryLinks = countryData?.countries ?? []
  const causalLinks = causalData?.causals ?? []

  // Every production method links all 244 countries, so the list is filtered
  // client-side rather than paged — the whole set is already in hand.
  const term = countrySearch.trim().toLowerCase()
  const visibleCountries = term
    ? countryLinks.filter(
        (c) => c.country.toLowerCase().includes(term) || c.isoCode2.toLowerCase().includes(term),
      )
    : countryLinks

  const linkedCountryIds = new Set(countryLinks.map((c) => c.countryId))
  const linkableCountries = countries.filter((c) => !linkedCountryIds.has(c.id))
  const linkedCausalIds = new Set(causalLinks.map((c) => c.causalId))
  const linkableCausals = causals.filter((c) => !linkedCausalIds.has(c.id))

  if (!method) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
  }

  const formatDate = (value: string | null) =>
    value ? new Date(value).toLocaleString() : '—'

  return (
    <div>
      <Link
        to="/payment-methods"
        className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        {t('paymentMethods.title')}
      </Link>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-semibold">{method.paymentMethod}</h2>
          <p className="text-sm text-muted-foreground">{method.paymentMethodType}</p>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge disabled={method.isDisabledByPlatform} />
          {canWrite && (
            <Button
              variant="outline"
              size="sm"
              disabled={toggleDisabled.isPending}
              onClick={() => toggleDisabled.mutate()}
            >
              {method.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
              {method.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
            </Button>
          )}
        </div>
      </div>

      <ErrorBanner message={error} />

      <Tabs
        value={tab}
        onValueChange={(value) => {
          searchParams.set('tab', String(value))
          setSearchParams(searchParams, { replace: true })
        }}
      >
        <TabsList>
          <Tab value="summary">{t('paymentMethods.tabs.summary')}</Tab>
          <Tab value="countries">
            {t('paymentMethods.tabs.countries')}
            <TabCount value={countryLinks.length} />
          </Tab>
          <Tab value="causals">
            {t('paymentMethods.tabs.causals')}
            <TabCount value={causalLinks.length} />
          </Tab>
        </TabsList>

        {/* ---- summary ---- */}
        <TabsPanel value="summary">
          {editing ? (
            <PaymentMethodForm
              value={form}
              onChange={setForm}
              onSubmit={() => save.mutate()}
              onCancel={() => {
                setEditing(false)
                setError('')
              }}
              types={paymentMethodTypes}
              pending={save.isPending}
              error={error}
              submitLabel={t('common.save')}
            />
          ) : (
            <>
              <Card className="max-w-3xl">
                <CardContent>
                  <dl className="grid grid-cols-[10rem_1fr] gap-x-6 gap-y-3 text-sm">
                <SummaryRow label={t('paymentMethods.columns.id')}>
                  <span className="font-mono text-xs">{method.paymentMethodId}</span>
                </SummaryRow>
                <SummaryRow label={t('paymentMethods.columns.type')}>
                  {method.paymentMethodType}
                </SummaryRow>
                <SummaryRow label={t('paymentMethods.columns.description')}>
                  {method.description || '—'}
                </SummaryRow>
                <SummaryRow label={t('paymentMethods.columns.logo')}>
                  <span className="font-mono text-xs">{method.logoPath || '—'}</span>
                </SummaryRow>
                <SummaryRow label={t('paymentMethods.columns.icon')}>
                  <span className="font-mono text-xs">{method.icon || '—'}</span>
                </SummaryRow>
                <SummaryRow label={t('paymentMethods.summary.relations')}>
                  {t('paymentMethods.summary.relationsValue', {
                    countries: method.countryCount,
                    causals: method.causalCount,
                  })}
                </SummaryRow>
                <SummaryRow label={t('common.columns.created')}>
                  {formatDate(method.creationDate)}
                </SummaryRow>
                    <SummaryRow label={t('paymentMethods.summary.lastModified')}>
                      {formatDate(method.lastModificationDate)}
                    </SummaryRow>
                  </dl>
                </CardContent>
              </Card>

              {canWrite && (
                <Button className="mt-6" variant="outline" size="sm" onClick={() => setEditing(true)}>
                  <Pencil />
                  {t('common.edit')}
                </Button>
              )}
            </>
          )}
        </TabsPanel>

        {/* ---- countries ---- */}
        <TabsPanel value="countries">
          <div className="mb-3 flex flex-wrap items-center justify-end gap-2">
            <div className="relative">
              <Search className="absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder={t('paymentMethods.countries.searchPlaceholder')}
                className="w-56 pl-8"
              />
            </div>
            {canWrite && (
              <>
                <Combobox
                  value={newCountry}
                  onValueChange={setNewCountry}
                  items={linkableCountries.map((c) => ({
                    value: String(c.id),
                    label: `${c.isoCode2} — ${c.name}`,
                  }))}
                  placeholder={t('paymentMethods.countries.addPlaceholder')}
                  className="w-56"
                />
                <Button
                  size="sm"
                  disabled={!newCountry || setCountry.isPending}
                  onClick={() =>
                    setCountry.mutate({
                      countryId: Number(newCountry),
                      isKycNeeded: false,
                      isProhibited: false,
                    })
                  }
                >
                  <Plus />
                  {t('common.add')}
                </Button>
              </>
            )}
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-20">{t('paymentMethods.countries.iso')}</TableHead>
                  <TableHead>{t('paymentMethods.countries.country')}</TableHead>
                  <TableHead className="w-32">{t('paymentMethods.countries.kyc')}</TableHead>
                  <TableHead className="w-32">{t('paymentMethods.countries.prohibited')}</TableHead>
                  {canWrite && <TableHead className="w-20 text-right" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleCountries.map((c) => (
                  <TableRow key={c.countryId}>
                    <TableCell className="font-mono text-xs">{c.isoCode2 || '—'}</TableCell>
                    <TableCell>{c.country || `#${c.countryId}`}</TableCell>
                    <TableCell>
                      <Switch
                        checked={c.isKycNeeded}
                        disabled={!canWrite || setCountry.isPending}
                        label={c.isKycNeeded ? t('common.yes') : t('common.no')}
                        onCheckedChange={(checked) =>
                          setCountry.mutate({
                            countryId: c.countryId,
                            isKycNeeded: checked,
                            isProhibited: c.isProhibited,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={c.isProhibited}
                        disabled={!canWrite || setCountry.isPending}
                        label={c.isProhibited ? t('common.yes') : t('common.no')}
                        onCheckedChange={(checked) =>
                          setCountry.mutate({
                            countryId: c.countryId,
                            isKycNeeded: c.isKycNeeded,
                            isProhibited: checked,
                          })
                        }
                      />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('paymentMethods.countries.remove')}
                            disabled={removeCountry.isPending}
                            onClick={() => removeCountry.mutate(c.countryId)}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {visibleCountries.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 5 : 4}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('paymentMethods.countries.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>

        {/* ---- causals ---- */}
        <TabsPanel value="causals">
          {canWrite && (
            <div className="mb-3 flex flex-wrap items-end justify-end gap-2">
              <Combobox
                value={newCausal.causalId}
                onValueChange={(v) => setNewCausal({ ...newCausal, causalId: v })}
                items={linkableCausals.map((c) => ({ value: String(c.id), label: c.name }))}
                placeholder={t('paymentMethods.causals.addPlaceholder')}
                className="w-56"
              />
              <Select
                value={newCausal.movementTypeId}
                onValueChange={(v) => setNewCausal({ ...newCausal, movementTypeId: v })}
                items={movementTypes.map((m) => ({ value: String(m.id), label: m.name }))}
                placeholder={t('paymentMethods.causals.noMovement')}
                className="w-40"
              />
              <Combobox
                value={newCausal.settingTypeId}
                onValueChange={(v) => setNewCausal({ ...newCausal, settingTypeId: v })}
                items={settingTypes.map((st) => ({ value: String(st.id), label: st.name }))}
                placeholder={t('paymentMethods.causals.settingPlaceholder')}
                className="w-56"
              />
              <Button
                size="sm"
                disabled={!newCausal.causalId || !newCausal.settingTypeId || setCausal.isPending}
                onClick={() =>
                  setCausal.mutate({
                    causalId: Number(newCausal.causalId),
                    movementTypeId: Number(newCausal.movementTypeId || 0),
                    settingTypeId: Number(newCausal.settingTypeId),
                  })
                }
              >
                <Plus />
                {t('common.add')}
              </Button>
            </div>
          )}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead>{t('paymentMethods.causals.causal')}</TableHead>
                  <TableHead className="w-40">{t('paymentMethods.causals.movement')}</TableHead>
                  <TableHead className="w-72">{t('paymentMethods.causals.setting')}</TableHead>
                  {canWrite && <TableHead className="w-20 text-right" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {causalLinks.map((c) => (
                  <TableRow key={c.causalId}>
                    <TableCell className="font-medium">{c.causal || `#${c.causalId}`}</TableCell>
                    <TableCell>
                      {canWrite ? (
                        <Select
                          value={String(c.movementTypeId || '')}
                          onValueChange={(v) =>
                            setCausal.mutate({
                              causalId: c.causalId,
                              movementTypeId: Number(v || 0),
                              settingTypeId: c.settingTypeId,
                            })
                          }
                          items={movementTypes.map((m) => ({ value: String(m.id), label: m.name }))}
                          placeholder={t('paymentMethods.causals.noMovement')}
                          className="w-36"
                        />
                      ) : (
                        c.movementType || '—'
                      )}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.settingType || `#${c.settingTypeId}`}
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('paymentMethods.causals.remove')}
                            disabled={removeCausal.isPending}
                            onClick={() => removeCausal.mutate(c.causalId)}
                          >
                            <Trash2 />
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {causalLinks.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 4 : 3}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('paymentMethods.causals.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsPanel>
      </Tabs>
    </div>
  )
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd>{children}</dd>
    </>
  )
}
